import React, { useState } from "react";
import { Download, ChevronDown, Settings2, X } from "lucide-react";
import { PRESETS, buildExport, downloadString } from "../lib/exportPresets";
import { toast } from "sonner";

// Export menu — replaces the single "Download" button with a dropdown of
// destination-aware presets (Asana, Excel, Notion, etc.) plus a "Custom…"
// dialog for power users who need fine-grained control over delimiter,
// encoding, line endings, and quote style.
//
// Designed to feel obvious: clicking the main label downloads with the
// current/default preset, clicking the chevron opens the menu.

export default function ExportButton({ headers, rows, fileName }) {
    const [open, setOpen] = useState(false);
    const [customOpen, setCustomOpen] = useState(false);
    const [defaultPreset, setDefaultPreset] = useState(() => {
        try {
            return localStorage.getItem("opencsv:last-export") || "plain";
        } catch {
            return "plain";
        }
    });

    const doExport = (presetId, overrides = {}) => {
        const preset = PRESETS.find((p) => p.id === presetId) || PRESETS[0];
        const data = [headers, ...rows];
        const out = buildExport(data, preset, fileName || "openCSV-export.csv", overrides);
        downloadString(out.filename, out.body, out.mime);
        try { localStorage.setItem("opencsv:last-export", preset.id); } catch {/* ignore */ }
        setDefaultPreset(preset.id);
        setOpen(false);
        toast.success(`Exported: ${out.filename}`);
    };

    const labelForDefault = (PRESETS.find((p) => p.id === defaultPreset) || PRESETS[0]).label;

    return (
        <div className="relative inline-flex" data-testid="export-button">
            <button
                onClick={() => doExport(defaultPreset)}
                className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm rounded-r-none"
                title={`Export as ${labelForDefault}`}
                data-testid="export-default-btn"
            >
                <Download size={14} />
                <span className="hidden sm:inline">Export</span>
            </button>
            <button
                onClick={() => setOpen(!open)}
                className="btn-brutal inline-flex items-center border-l-0 border border-ink/20 bg-white px-2 py-2 ui-heading text-sm rounded-l-none"
                aria-label="Open export menu"
                data-testid="export-menu-toggle"
            >
                <ChevronDown size={14} />
            </button>

            {open && (
                <>
                    {/* click-outside catcher */}
                    <button
                        type="button"
                        className="fixed inset-0 z-30 cursor-default"
                        onClick={() => setOpen(false)}
                        aria-hidden
                        tabIndex={-1}
                    />
                    <div
                        className="absolute top-full right-0 mt-1 z-40 min-w-[280px] bg-white border border-ink/20 shadow-lg py-1"
                        data-testid="export-menu"
                    >
                        <div className="ui-heading text-[10px] uppercase tracking-widest text-ink-muted px-3 pt-2 pb-1">/ export as</div>
                        {PRESETS.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => doExport(p.id)}
                                className="w-full text-left px-3 py-2 hover:bg-paper flex flex-col gap-0.5"
                                data-testid={`export-preset-${p.id}`}
                            >
                                <span className="text-sm font-medium text-ink">{p.label}</span>
                                <span className="text-[11px] text-ink-muted leading-snug">{p.tagline}</span>
                            </button>
                        ))}
                        <div className="border-t border-ink/10 mt-1 pt-1">
                            <button
                                onClick={() => { setOpen(false); setCustomOpen(true); }}
                                className="w-full text-left px-3 py-2 hover:bg-paper flex items-center gap-2 text-sm text-cobalt font-medium"
                                data-testid="export-custom-open"
                            >
                                <Settings2 size={14} /> Custom format…
                            </button>
                        </div>
                    </div>
                </>
            )}

            {customOpen && (
                <CustomFormatDialog
                    onClose={() => setCustomOpen(false)}
                    onExport={(overrides) => {
                        doExport("plain", overrides);
                        setCustomOpen(false);
                    }}
                />
            )}
        </div>
    );
}

// Custom format dialog — exposed for power users who need a delimiter or
// encoding combo that no preset covers. Built on the "plain" preset and
// overrides whichever fields the user changes.
function CustomFormatDialog({ onClose, onExport }) {
    const [delimiter, setDelimiter] = useState(",");
    const [newline, setNewline] = useState("\n");
    const [bom, setBom] = useState(false);
    const [quotes, setQuotes] = useState("smart");

    const handleSubmit = (e) => {
        e.preventDefault();
        const quotesVal = quotes === "all" ? "all" : quotes === "none" ? false : true;
        onExport({ delimiter, newline, bom, quotes: quotesVal });
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="custom-format-dialog">
            <form onSubmit={handleSubmit} className="bg-white border border-ink/20 shadow-lg w-full max-w-md">
                <div className="flex items-center justify-between border-b border-ink/10 px-5 py-3">
                    <h3 className="font-display font-black tracking-tighter text-xl">Custom export</h3>
                    <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink" aria-label="Close dialog" data-testid="custom-format-close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4 text-sm">
                    <div>
                        <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft block mb-1">Delimiter</label>
                        <div className="flex gap-2">
                            {[[",", "Comma"], [";", "Semicolon"], ["\t", "Tab"], ["|", "Pipe"]].map(([val, name]) => (
                                <button
                                    type="button"
                                    key={val}
                                    onClick={() => setDelimiter(val)}
                                    className={`flex-1 border px-2 py-1.5 text-xs ${delimiter === val ? "bg-ink text-white border-ink" : "border-ink/20 bg-white"}`}
                                    data-testid={`custom-delim-${name.toLowerCase()}`}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft block mb-1">Line endings</label>
                        <div className="flex gap-2">
                            {[["\n", "LF (Mac/Linux)"], ["\r\n", "CRLF (Windows)"]].map(([val, name]) => (
                                <button
                                    type="button"
                                    key={val}
                                    onClick={() => setNewline(val)}
                                    className={`flex-1 border px-2 py-1.5 text-xs ${newline === val ? "bg-ink text-white border-ink" : "border-ink/20 bg-white"}`}
                                    data-testid={`custom-newline-${val === "\n" ? "lf" : "crlf"}`}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft block mb-1">Quote style</label>
                        <select
                            value={quotes}
                            onChange={(e) => setQuotes(e.target.value)}
                            className="w-full border border-ink/20 px-2 py-1.5 text-sm bg-white"
                            data-testid="custom-quotes"
                        >
                            <option value="smart">Smart (only quote when needed)</option>
                            <option value="all">Always quote every field</option>
                            <option value="none">Never quote</option>
                        </select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={bom}
                            onChange={(e) => setBom(e.target.checked)}
                            data-testid="custom-bom"
                        />
                        <span>Include UTF-8 BOM <span className="text-ink-muted">(for Excel on Windows, Notion)</span></span>
                    </label>
                </div>

                <div className="border-t border-ink/10 px-5 py-3 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-ink/20" data-testid="custom-format-cancel">Cancel</button>
                    <button type="submit" className="btn-brutal px-4 py-2 bg-cobalt text-white border border-cobalt ui-heading text-sm font-semibold" data-testid="custom-format-export">Export</button>
                </div>
            </form>
        </div>
    );
}
